/**
 * Resolução de ID WhatsApp + persistência de edit/delete no Supabase.
 * Mantém useChatActions só na orquestração UI.
 */
import { supabase } from '../../../lib/supabase';
import { extractNumber } from '../../../utils/phoneUtils';
import { stripMessageSignature } from '../../../utils/messageSignature';
import type { ChatMessage } from '../hooks/types';
import { notifyPartykitLocalMessage } from '../utils/partykitSocket';
import { ChatSendPendingError } from '../../../services/modules/chatSendReceipt';

const PLACEHOLDER_ID = /^(temp-|sent-)/;
const UNIQUE_VIOLATION = '23505';
const MSG_ID_UNAVAILABLE = 'Aguarde a confirmação do envio antes de editar ou apagar a mensagem.';
const isPlaceholder = (id: string) => PLACEHOLDER_ID.test(id);
const normalizeContent = (text: string | null | undefined) => stripMessageSignature(text || '').trim();

export function messageTimestampMs(message?: ChatMessage): number | undefined {
    const value = message?.timestamp?.getTime();
    return value != null && Number.isFinite(value) ? value : undefined;
}

async function fetchMyRecentMessages<T extends string>(
    conversationDbId: string,
    columns: T,
    limit: number,
) {
    const { data, error } = await supabase
        .from('chat_messages')
        .select(columns)
        .eq('conversation_id', conversationDbId)
        .eq('sender', 'me')
        .order('timestamp', { ascending: false })
        .limit(limit);
    return { data, error };
}

/** temp-* → id real; se o webhook já gravou o real, apaga o órfão. */
export async function reconcileTempMessageId(params: {
    conversationId: string | null;
    tempId: string;
    realId: string;
    patch?: Record<string, unknown>;
    /** Após id real, avisa peers (evita notify com temp-*). */
    peerNotify?: {
        organizationId: string;
        content?: string;
        messageType?: string;
        messageTimestamp?: string | Date;
    };
}): Promise<void> {
    const { conversationId, tempId, realId, patch, peerNotify } = params;
    if (!conversationId || !tempId || realId === tempId) return;

    const { error } = await supabase
        .from('chat_messages')
        .update({ message_id: realId, ...patch })
        .eq('message_id', tempId)
        .eq('conversation_id', conversationId);

    if (error) {
        if (error.code !== UNIQUE_VIOLATION) throw error;
        await supabase
            .from('chat_messages')
            .delete()
            .eq('message_id', tempId)
            .eq('conversation_id', conversationId);
    }

    if (peerNotify?.organizationId) {
        void notifyPartykitLocalMessage({
            conversationId,
            organizationId: peerNotify.organizationId,
            messageId: realId,
            fromMe: true,
            content: peerNotify.content,
            messageType: peerNotify.messageType,
            messageTimestamp: peerNotify.messageTimestamp,
        });
    }
}

/** Marca temp como failed mesmo sem convId do persist. */
export async function markTempMessageFailed(tempId: string, conversationId?: string | null, status: 'failed' | 'uncertain' = 'failed'): Promise<void> {
    let query = supabase.from('chat_messages').update({ status }).eq('message_id', tempId);
    if (conversationId) query = query.eq('conversation_id', conversationId);
    await query;
}

/** Aguarda persist em paralelo sem engolir o erro original do send. */
export async function awaitPersistSafe(
    persistPromise: Promise<string | null>,
): Promise<string | null> {
    try {
        return await persistPromise;
    } catch {
        return null;
    }
}

/** Persiste campos da mensagem — tenta conversation_id, depois organization_id. */
export async function persistMessageInDb(
    conversationDbId: string,
    organizationId: string | undefined,
    messageIds: string[],
    payload: Record<string, unknown>,
): Promise<boolean> {
    const uniqueIds = [...new Set(messageIds.filter(Boolean))];

    for (const id of uniqueIds) {
        const { data, error } = await supabase.from('chat_messages').update(payload)
            .eq('conversation_id', conversationDbId).eq('message_id', id).select('id');
        if (error) throw error;
        if (data?.length) return true;
    }
    return false;
}

/** Persiste edição — message_id, depois match por conteúdo original. */
export async function persistEditedMessageInDb(
    conversationDbId: string,
    organizationId: string | undefined,
    messageIds: string[],
    originalText: string | undefined,
    signedText: string,
): Promise<boolean> {
    const payload = { content: signedText, is_edited: true };
    if (await persistMessageInDb(conversationDbId, organizationId, messageIds, payload)) {
        return true;
    }

    const hints = new Set<string>();
    if (originalText) {
        hints.add(normalizeContent(originalText));
        hints.add(originalText.trim());
    }
    if (!hints.size) return false;

    const { data: rows } = await fetchMyRecentMessages(conversationDbId, 'id, content', 50);
    const match = rows?.find((row) => {
        const normalized = normalizeContent(row.content);
        return hints.has(normalized) || hints.has((row.content || '').trim());
    });
    if (!match) return false;

    const { data } = await supabase
        .from('chat_messages')
        .update(payload)
        .eq('id', match.id)
        .select('id');
    return Boolean(data?.length);
}

/** Resolve DB + WhatsApp id da mesma mensagem (nunca a anterior às cegas). */
export async function resolveMutationTargets(
    conversationDbId: string | null,
    activeConversationId: string,
    organizationId: string | undefined,
    messageId: string,
    contentHint?: string,
    timestampMs?: number,
) {
    // Texto/horário não identificam uma mensagem (vários atendentes podem enviar "Ok").
    if (isPlaceholder(messageId)) throw new Error(MSG_ID_UNAVAILABLE);
    if (!conversationDbId) throw new Error('Aguarde carregar a conversa antes de alterar a mensagem.');
    const dbId = conversationDbId;
    const resolvedId = messageId;

    return {
        dbId,
        resolvedId,
        ids: [...new Set([messageId, resolvedId].filter(Boolean))],
        number: extractNumber(activeConversationId),
    };
}


export type EnqueueChatSend = <T>(send: (onAccepted: () => void) => Promise<T>) => Promise<T>;

/** Sequencia envios em andamento; mensagens continuam persistidas no banco. Sem retry de POST. */
export function createChatSendQueue() {
    const tails = new Map<string, Promise<unknown>>();
    return <T>(key: string, send: (onAccepted: () => void) => Promise<T>): Promise<T> => {
        let release!: () => void;
        const gate = new Promise<void>(resolve => { release = resolve; });
        // Aceito na fila → libera o próximo envio sem esperar o recibo final.
        const onAccepted = () => { release(); };
        const pending = (tails.get(key) ?? Promise.resolve()).catch(() => undefined).then(() => send(onAccepted));
        tails.set(key, gate);
        const cleanup = () => { release(); if (tails.get(key) === gate) tails.delete(key); };
        // Sempre libera o gate — ChatSendPendingError antes do accept travava o composer.
        // then(ok, err) no void: não vira unhandledRejection; o caller ainda recebe o reject.
        void pending.then(cleanup, cleanup);
        return pending;
    };
}
